import { useDocumentMeta } from '../lib/useDocumentMeta';

type Kind = 'privacy' | 'terms' | 'coppa' | 'gdpr-k' | 'subprocessors';

interface Props {
  kind: Kind;
}

const LAST_UPDATED = 'April 27, 2026';

const TITLES: Record<Kind, { title: string; description: string }> = {
  privacy: { title: 'Privacy Policy', description: 'How PromptGPT collects, uses, and protects your data.' },
  terms: { title: 'Terms of Service', description: 'The terms that govern your use of PromptGPT.' },
  coppa: { title: 'COPPA Disclosure', description: 'How PromptGPT Kids complies with the Children’s Online Privacy Protection Act.' },
  'gdpr-k': { title: 'GDPR-K Notice', description: 'How PromptGPT Kids handles personal data of children under EU and UK law.' },
  subprocessors: { title: 'Sub-processors', description: 'Third parties that process data on behalf of PromptGPT.' },
};

export default function LegalPage({ kind }: Props) {
  const meta = TITLES[kind];
  useDocumentMeta({
    title: meta.title,
    description: meta.description,
    canonical: `https://promptgpt.ai/${kind}`,
  });

  return (
    <div className="container-app py-16 max-w-3xl">
      <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-3">Legal</div>
      <h1 className="font-display text-5xl mb-2">{meta.title}</h1>
      <p className="text-sm text-ink-500 mb-10">Last updated {LAST_UPDATED}</p>

      <div className="prose prose-ink max-w-none space-y-6 text-ink-700 leading-relaxed">
        {kind === 'privacy' && <PrivacyContent />}
        {kind === 'terms' && <TermsContent />}
        {kind === 'coppa' && <CoppaContent />}
        {kind === 'gdpr-k' && <GdprKContent />}
        {kind === 'subprocessors' && <SubprocessorsContent />}
      </div>
    </div>
  );
}

function PrivacyContent() {
  return (
    <>
      <Section title="What we collect">
        We collect the email address you sign up with, your profile details (full name, role, learning goals), and the lesson, workflow, and prompt activity you produce inside PromptGPT. Payment details are handled by Stripe; we never see your card number.
      </Section>
      <Section title="How we use it">
        We use your data to operate the product, personalize your dashboard, run AI features on prompts you submit, send essential service emails, and improve the platform. We do not sell personal data.
      </Section>
      <Section title="AI providers">
        Prompts you submit to the AI Coach and to guided workflows are processed by third-party model providers (OpenAI). Do not paste secrets, regulated personal data, or confidential client information.
      </Section>
      <Section title="Children under 13">
        For accounts created by parents or guardians for children under 13, see our <a className="underline" href="/coppa">COPPA disclosure</a>. For EU and UK children under 16, see our <a className="underline" href="/gdpr-k">GDPR-K notice</a>.
      </Section>
      <Section title="Cookies">
        We use cookies for authentication and basic analytics. You can clear them at any time from your browser.
      </Section>
      <Section title="Data retention and deletion">
        Your account data is retained while your account is active. Email support@promptgpt.ai to request export or permanent deletion; we will respond within 30 days.
      </Section>
      <Section title="Contact">Privacy questions: support@promptgpt.ai.</Section>
    </>
  );
}

function TermsContent() {
  return (
    <>
      <Section title="Acceptable use">
        You may use PromptGPT for lawful learning and work purposes. Do not use the platform to generate content that is illegal, harassing, or that violates third-party rights. Do not attempt to reverse engineer or scrape the service.
      </Section>
      <Section title="Subscriptions and billing">
        Paid plans renew monthly until you cancel. Starter is $5 per month; Pro is $10 per month. You can cancel any time from Settings; access continues until the end of the current billing period. We do not offer refunds for partial months.
      </Section>
      <Section title="Plan changes and pricing">
        We may change pricing or plan limits with at least 30 days notice via email. Continued use after a price change confirms acceptance.
      </Section>
      <Section title="Your content">
        You retain ownership of prompts and outputs you create. You grant us a limited license to host and display them inside your account. If you opt into a public profile, you agree the work you publish there can be viewed by anyone.
      </Section>
      <Section title="AI outputs">
        AI outputs can be inaccurate. You are responsible for reviewing them before relying on them in real work.
      </Section>
      <Section title="Termination">
        We may suspend or terminate accounts that violate these terms. You may close your account anytime.
      </Section>
      <Section title="Limitation of liability">
        PromptGPT is provided "as is." To the extent permitted by law, our aggregate liability is limited to the amount you paid in the prior twelve months.
      </Section>
      <Section title="Contact">Questions: support@promptgpt.ai.</Section>
    </>
  );
}

function CoppaContent() {
  return (
    <>
      <Section title="Who this applies to">
        PromptGPT Kids is offered through a parent or guardian account. Children under 13 in the United States never sign up directly. A guardian creates a kid profile with first name and age only; no email, phone number, or address is collected from the child.
      </Section>
      <Section title="What we collect from a child">
        First name (or nickname), age, age band, and the content of activities the child completes (prompts, AI responses, refusals). We do not collect persistent identifiers tied to the child outside of the guardian's household, behavioral advertising signals, or precise location.
      </Section>
      <Section title="Verifiable parental consent">
        At the moment a guardian creates a kid profile we record an explicit consent affirmation tied to the guardian's authenticated account. The consent record stores the guardian's user ID, the kid profile ID, the consent type ("coppa"), and the timestamp. Guardians can revoke consent and delete the kid profile at any time from the guardian dashboard.
      </Section>
      <Section title="No advertising, no profiling">
        We do not use kid data for advertising, behavioral profiling, or training models. Kid transcripts are never used for model fine-tuning.
      </Section>
      <Section title="Parent rights">
        From the guardian dashboard a parent can: view every transcript and refusal, export an archive (JSON), pause or delete the kid profile, and adjust time caps and quiet hours. Email support@promptgpt.ai for help.
      </Section>
      <Section title="Operator">
        The operator under COPPA is PromptGPT, Inc. Contact: privacy@promptgpt.ai.
      </Section>
    </>
  );
}

function GdprKContent() {
  return (
    <>
      <Section title="Lawful basis">
        For children in the EU and UK, the lawful basis for processing personal data is consent given by the holder of parental responsibility (Article 8 GDPR; UK GDPR). Consent is captured at kid profile creation and stored against the guardian's authenticated account.
      </Section>
      <Section title="Age threshold">
        We treat the threshold as 16 unless a member state has lowered it. Guardians can revoke consent at any time, which results in deletion of the kid profile and associated transcripts within 30 days.
      </Section>
      <Section title="Data minimisation">
        We collect first name, age, and activity content only. We do not collect special category data, location, or device identifiers tied to the child.
      </Section>
      <Section title="Rights">
        Data subjects (acting through their guardian for children) have rights of access, rectification, erasure, restriction, portability, and objection. Email privacy@promptgpt.ai to exercise them.
      </Section>
      <Section title="International transfers">
        We rely on Standard Contractual Clauses with our sub-processors for transfers outside the EEA and UK. See our <a className="underline" href="/subprocessors">sub-processor list</a>.
      </Section>
      <Section title="Supervisory authority">
        EU residents may lodge complaints with their local DPA. UK residents may lodge complaints with the ICO.
      </Section>
    </>
  );
}

function SubprocessorsContent() {
  const rows: Array<{ name: string; purpose: string; location: string }> = [
    { name: 'Supabase, Inc.', purpose: 'Postgres database, authentication, edge functions, storage', location: 'United States' },
    { name: 'OpenAI, L.L.C.', purpose: 'Model inference for lessons, workflows, and kids activities (no training on customer data)', location: 'United States' },
    { name: 'Stripe, Inc.', purpose: 'Subscription billing and payments', location: 'United States' },
    { name: 'Resend (Drift Inc.)', purpose: 'Transactional email (receipts, alerts, password resets)', location: 'United States' },
    { name: 'Netlify, Inc.', purpose: 'Frontend hosting and CDN', location: 'United States' },
  ];
  return (
    <>
      <Section title="Current sub-processors">
        We engage the following sub-processors. New additions are announced with 30 days' notice.
      </Section>
      <div className="not-prose overflow-x-auto border border-ink-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-warm-50 text-ink-700">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Vendor</th>
              <th className="text-left px-4 py-2 font-semibold">Purpose</th>
              <th className="text-left px-4 py-2 font-semibold">Location</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-ink-100">
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 text-ink-700">{r.purpose}</td>
                <td className="px-4 py-3 text-ink-600">{r.location}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Section title="Subscribe to changes">
        Email security@promptgpt.ai to be notified when this list changes.
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-display text-2xl mb-3 text-ink-900">{title}</h2>
      <div className="text-ink-700 leading-relaxed">{children}</div>
    </div>
  );
}
